import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function BestPlacesToBuyFishInVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-place-to-buy-fish-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Best Fresh Seafood Markets",
    "section-2": "Top Aquarium Fish Stores",
    "local-regulations": "Local Regulations / Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // NOTE: Use relevant references from the conversation
  const referencesData = [
    {
      url: "https://ocean.org/what-we-do/ocean-wise/",
      label: "Ocean Wise Seafood Program (Vancouver Aquarium)",
    },
    {
      url: "https://pac.dfo-mpo.gc.ca",
      label: "Fisheries and Oceans Canada (DFO) – Pacific Region",
    },
    {
      url: "https://bc.gov.ca",
      label: "BC Government - Controlled Alien Species Regulations",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed; each location's "id" must match
  // the ref used in the text <p ref={(el) => setSpotRef("some-id", el)}>
  // Coordinates must match the text content for each location.

  const locations = [
    // FRESH SEAFOOD LOCATIONS
    {
      id: "granville-island-public-market",
      lat: 49.2726,
      lng: -123.1352,
      popupText: "Granville Island Public Market – Seafood Vendors",
    },
    {
      id: "the-lobster-man",
      lat: 49.2712,
      lng: -123.1365,
      popupText: "The Lobster Man (Granville Island)",
    },
    {
      id: "7-seas-fish-market",
      lat: 49.2685,
      lng: -123.1575,
      popupText: "7 Seas Fish Market (Kitsilano)",
    },
    {
      id: "t-t-supermarket-chinatown",
      lat: 49.2790,
      lng: -123.1090,
      popupText: "T & T Supermarket (Chinatown)",
    },
    {
      id: "fresh-ideas-start-here",
      lat: 49.2640,
      lng: -123.1720,
      popupText: "Fresh Ideas Start Here (F.I.S.H.) – Kitsilano",
    },
    {
      id: "false-creek-fishermens-wharf",
      lat: 49.2725,
      lng: -123.1400,
      popupText: "False Creek Fishermen’s Wharf",
    },
    {
      id: "steveston-public-fish-sales-float",
      lat: 49.1243,
      lng: -123.1865,
      popupText: "Steveston Public Fish Sales Float (Richmond)",
    },

    // AQUARIUM FISH STORES
    {
      id: "aquariums-west",
      lat: 49.2765,
      lng: -123.1150,
      popupText: "Aquariums West (Downtown Vancouver)",
    },
    {
      id: "king-ed-pet-centre",
      lat: 49.2160,
      lng: -122.9480,
      popupText: "King Ed Pet Centre (Burnaby)",
    },
    {
      id: "aprils-aquarium",
      lat: 49.2120,
      lng: -123.1090,
      popupText: "April’s Aquarium (South Vancouver)",
    },
    {
      id: "fantasea-aquatics",
      lat: 49.2480,
      lng: -123.0550,
      popupText: "Fantasea Aquatics (East Vancouver)",
    },
    {
      id: "noahs-pet-ark",
      lat: 49.2640,
      lng: -123.1680,
      popupText: "Noah’s Pet Ark (Kitsilano)",
    },
    {
      id: "island-pets-aquarium",
      lat: 49.1740,
      lng: -123.1360,
      popupText: "Island Pets & Aquarium (Richmond)",
    },
  ];

  // Refs for scroll anchors
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Best Places to Buy Fish in Vancouver</title>
        <meta
          name="description"
          content="Your comprehensive guide to the best places to buy fresh seafood and aquarium fish in Vancouver. Includes local regulations, FAQs, and more."
        />
        <meta
          name="keywords"
          content="seafood, fish markets, aquarium fish, Vancouver"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-place-to-buy-fish-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Best Places to Buy Fish in Vancouver"
        />
        <meta
          property="og:description"
          content="A handy guide on fresh seafood markets and aquarium fish stores in Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-place-to-buy-fish-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-place-to-buy-fish-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Places to Buy Fish in Vancouver"
        />
        <meta
          name="twitter:description"
          content="From fresh sockeye salmon to exotic aquarium fish, here's where to find them in Vancouver."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-place-to-buy-fish-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Fresh Fish in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s coastal location means there’s a wide variety of
              fish—from freshly caught salmon and halibut for your dinner plate,
              to vibrant, ethically sourced aquarium fish for your home tank.
              This article will explore the best places in Vancouver to buy fish
              for cooking and for keeping, along with key local regulations and
              best practices. Buying from reputable vendors ensures quality,
              sustainability, and the health of our local ecosystems—whether
              you’re aiming for a seafood feast or planning your next aquarium
              setup.
            </p>

            {/* SECTION: Best Fresh Seafood Markets */}
            <h2 id="section-1">Best Fresh Seafood Markets</h2>
            <p>
              These top-notch seafood markets and wharfs offer the freshest
              local catches like salmon, halibut, spot prawns, crab, and more.
              Many are Ocean Wise partners, indicating sustainable and ethical
              sourcing practices.
            </p>

            <p
              ref={(el) => setSpotRef("granville-island-public-market", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Granville Island Public Market</strong> —{" "}
              <Marker
                coordY={49.2726}
                coordX={-123.1352}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              This bustling hub (1669 Johnston St) hosts several seafood stalls
              open daily (9am–6pm). Check out Longliner Seafoods or The Lobster
              Man kiosk for wild salmon, halibut, spot prawns (in season),
              shellfish, and more. Prices range from moderate to premium, and
              you’ll find many Ocean Wise recommendations here.
            </p>

            <p
              ref={(el) => setSpotRef("the-lobster-man", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Lobster Man (Granville Island)</strong> —{" "}
              <Marker
                coordY={49.2712}
                coordX={-123.1365}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              Operating since 1977 at 1807 Mast Tower Rd, The Lobster Man
              specializes in live shellfish—lobsters, Dungeness crab, oysters,
              clams, and more. Open daily (9am–6pm), they focus on fresh,
              seasonal BC specialties like spot prawns and can pack live lobster
              for travel.
            </p>

            <p
              ref={(el) => setSpotRef("7-seas-fish-market", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>7 Seas Fish Market (Kitsilano)</strong> —{" "}
              <Marker
                coordY={49.2685}
                coordX={-123.1575}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              Located at 2328 W 4th Ave, this family-owned fishmonger has been in
              business for over 55 years. Look for BC salmon, halibut, lingcod,
              shellfish, and house-made chowders. Most products are Ocean Wise
              labeled, with prices in the mid–high range.
            </p>

            <p
              ref={(el) => setSpotRef("t-t-supermarket-chinatown", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>T &amp; T Supermarket (Chinatown)</strong> —{" "}
              <Marker
                coordY={49.2790}
                coordX={-123.1090}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              Located at 179 Keefer Pl, T &amp; T is known for its large seafood
              section with live tanks (lobster, crab, spot prawns). They have a
              wide range of fish (whole or filleted), plus sashimi-grade options.
              Prices can be more affordable than smaller boutiques, though not
              all items are Ocean Wise.
            </p>

            <p
              ref={(el) => setSpotRef("fresh-ideas-start-here", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Fresh Ideas Start Here (F.I.S.H.) – Kitsilano</strong> —{" "}
              <Marker
                coordY={49.2640}
                coordX={-123.1720}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              Situated at 2959 W Broadway, F.I.S.H. offers premium,
              sustainable seafood with a focus on traceability and Ocean Wise
              options. They specialize in sashimi-grade tuna and salmon, along
              with local specialties like spot prawns (in season). Slightly
              higher prices, but top-quality offerings.
            </p>

            <p
              ref={(el) => setSpotRef("false-creek-fishermens-wharf", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>False Creek Fishermen’s Wharf</strong> —{" "}
              <Marker
                coordY={49.2725}
                coordX={-123.1400}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              Found at 1505 West 1st Ave near Granville Island, you can buy
              direct from local fishermen (particularly spot prawns in May).
              Availability varies by season—check for weekend dock sales. Great
              prices for extremely fresh catch. Bring cash and a cooler.
            </p>

            <p
              ref={(el) => setSpotRef("steveston-public-fish-sales-float", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Steveston Public Fish Sales Float (Richmond)</strong> —{" "}
              <Marker
                coordY={49.1243}
                coordX={-123.1865}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              Located at Steveston Harbour (Bayview St &amp; 2nd Ave), this
              float is often bustling on weekends. Fishermen sell whole salmon,
              halibut, crabs, and more directly off their boats. Prices are
              typically lower than retail, but fish are sold whole (no on-site
              filleting). Get there early for the best selection.
            </p>

            {/* SECTION: Top Aquarium Fish Stores */}
            <h2 id="section-2">Top Aquarium Fish Stores</h2>
            <p>
              Vancouver also has a vibrant aquarium hobby scene. These shops are
              known for healthy livestock, knowledgeable staff, and a wide
              selection of both freshwater and saltwater species.
            </p>

            <p
              ref={(el) => setSpotRef("aquariums-west", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Aquariums West</strong> —{" "}
              <Marker
                coordY={49.2765}
                coordX={-123.1150}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              Located at 796 Beatty St, near BC Place. This family-run shop has
              been around for decades. Specializing in freshwater fish (tetras,
              cichlids, shrimp, etc.) and some saltwater. Knowledgeable staff,
              well-maintained display tanks, and a good selection of live
              aquatic plants. Slightly premium prices, but top-notch quality.
            </p>

            <p
              ref={(el) => setSpotRef("king-ed-pet-centre", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>King Ed Pet Centre (Burnaby)</strong> —{" "}
              <Marker
                coordY={49.2160}
                coordX={-122.9480}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              A massive, two-story pet supply store at 7377 Kingsway. It carries
              both freshwater and saltwater fish, plus reptiles, birds, and
              small mammals. Wide variety, competitive prices, but staff
              expertise varies since it’s a general pet store. Ideal if you’re
              shopping for multiple pets or want a large selection.
            </p>

            <p
              ref={(el) => setSpotRef("aprils-aquarium", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>April’s Aquarium (South Vancouver)</strong> —{" "}
              <Marker
                coordY={49.2120}
                coordX={-123.1090}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              Formerly known as April’s Aquarium, at 38 E 69th Ave (also a
              Burnaby location). Known for high-quality freshwater fish (rare
              bettas, dwarf cichlids, etc.) and a decent saltwater section.
              Curated selection, helpful staff, and a warm community feel. Mid-
              to high-price range, but worth it for unique species.
            </p>

            <p
              ref={(el) => setSpotRef("fantasea-aquatics", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Fantasea Aquatics (East Vancouver)</strong> —{" "}
              <Marker
                coordY={49.2480}
                coordX={-123.0550}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              A smaller, family-run store at 2495 Kingsway. Specializes in
              exotic freshwater fish like killifish, rare plecos, and nano
              species. Very reasonable pricing, though the shop can look
              cluttered. If you’re a serious hobbyist seeking unusual finds,
              check here first.
            </p>

            <p
              ref={(el) => setSpotRef("noahs-pet-ark", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Noah’s Pet Ark (Kitsilano)</strong> —{" "}
              <Marker
                coordY={49.2640}
                coordX={-123.1680}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              At 2886 W Broadway, this cozy neighborhood shop offers a broad
              range of community fish (tetras, goldfish, angelfish). Also sells
              dog/cat supplies. Pricing is fair, and staff are friendly. Great
              for Kitsilano residents who need pet essentials along with fish
              supplies.
            </p>

            <p
              ref={(el) => setSpotRef("island-pets-aquarium", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Island Pets &amp; Aquarium (Richmond)</strong> —{" "}
              <Marker
                coordY={49.1740}
                coordX={-123.1360}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              Found at 5335 No. 3 Road, it’s one of the largest aquarium-centric
              stores in the area. Extensive saltwater and freshwater sections,
              including corals, reef-safe fish, and big selection of tanks and
              supplies. Pricing is moderate, often offering sales on equipment.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              <strong>For Seafood Buyers:</strong> British Columbia enforces
              strict fisheries regulations to protect local species. Only buy
              from licensed commercial fishers when purchasing directly at the
              docks. Look for the{" "}
              <em>Ocean Wise</em> symbol indicating sustainable options. 
              Some species (e.g., female Dungeness crabs, undersized crabs) are protected and 
              won’t be sold by reputable vendors. When possible, choose local, 
              in-season catches (e.g., spot prawns in May, wild salmon in summer).
            </p>
            <p>
              <strong>For Aquarium Owners:</strong> BC’s Controlled Alien
              Species regulations prohibit certain invasive fish (e.g.,
              snakeheads). Always buy from trusted pet stores that avoid
              illegally sourced or endangered species. Never release aquarium
              fish into local waters—it’s harmful and illegal. Prioritize
              captive-bred fish for sustainability. Research tank requirements
              before purchasing and ensure you have proper filtration, cycling,
              and water conditions.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Where can I find sashimi-grade fish for homemade sushi?</h3>
              <p>
                Many local fishmongers (like T&amp;T or Fresh Ideas Start Here)
                label “sashimi-grade” or “sushi-grade” fish. Alternatively, you
                can deep-freeze fresh, high-quality fish to kill parasites. Ask
                the fishmonger for details on freezing protocols.
              </p>

              <h3>2. What’s the best way to transport my fish (seafood or aquarium) home?</h3>
              <p>
                Keep seafood chilled in a cooler or insulated bag. For aquarium
                fish, shops will bag them with oxygen—try to get them home
                within 1-2 hours. Avoid temperature extremes and acclimate them
                properly to your tank.
              </p>

              <h3>3. How do I ensure the fish I buy is sustainable?</h3>
              <p>
                Look for Ocean Wise, MSC, or ASC certifications. Ask vendors
                about their sourcing. Buying local and in-season typically helps
                reduce environmental impact.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you’re craving fresh salmon for dinner or seeking a
              show-stopping angelfish for your reef tank, Vancouver offers a
              wealth of options. Reputable fishmongers and aquarium shops
              prioritize quality and sustainability, ensuring you get healthy,
              ethically sourced fish. By following local regulations, being
              mindful of best practices, and supporting trustworthy vendors,
              you’ll enjoy the best of Vancouver’s fish offerings—while helping
              preserve the region’s vibrant aquatic life.
            </p>

            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}